import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Award, Check, Copy } from 'lucide-react';
import type { RegistrationStatus } from '@ready/shared';
import { useApi, type EventAttendees, type EventDraft } from '@/lib/api';
import { formatDate, formatSessionTime } from '@/lib/dates';
import { Badge, Button, Card, ErrorText } from '@/components/ui';
import { sessionLabel } from './authoring-sessions';

/*
 * `/events/:eventId/attendees`: who registered, who's waiting, and who came.
 * Hosts mark attendance per session (on top of self check-in with the code).
 */

type Attendee = EventAttendees['attendees'][number];

const FILTERS: { key: RegistrationStatus; label: string }[] = [
  { key: 'registered', label: 'Registered' },
  { key: 'waitlisted', label: 'Waitlist' },
  { key: 'cancelled', label: 'Cancelled' },
];

const STATUS_LABELS: Record<RegistrationStatus, string> = {
  registered: 'Registered',
  waitlisted: 'Waitlisted',
  cancelled: 'Cancelled',
};

export function EventAttendeesPage() {
  const { eventId = '' } = useParams();
  const api = useApi();
  const event = useQuery({ queryKey: ['event', eventId], queryFn: () => api.event(eventId) });
  const attendees = useQuery({
    queryKey: ['eventAttendees', eventId],
    queryFn: () => api.eventAttendees(eventId),
  });
  if (event.isPending || attendees.isPending)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (event.isError) return <ErrorText error={event.error} />;
  if (attendees.isError) return <ErrorText error={attendees.error} />;
  return <Attendees event={event.data} data={attendees.data} />;
}

function Attendees({ event, data }: { event: EventDraft; data: EventAttendees }) {
  const [filter, setFilter] = useState<RegistrationStatus>('registered');
  const sessions = [...event.sessions].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const count = (s: RegistrationStatus) => data.attendees.filter((a) => a.status === s).length;
  const registered = count('registered');
  const waitlisted = count('waitlisted');
  const shown = data.attendees.filter((a) => a.status === filter);
  const emails = data.attendees.filter((a) => a.status === 'registered').map((a) => a.email);

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/events/${event.id}/edit`}
          className="-my-2 inline-block py-2 text-sm text-muted-foreground hover:text-foreground"
        >
          ← {event.title}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold">Attendees</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {registered} registered
          {data.capacity !== null ? ` of ${data.capacity} seats` : ''}
          {waitlisted > 0 ? ` · ${waitlisted} on the waitlist` : ''}
          {event.status === 'cancelled' ? ' · This event was cancelled' : ''}
        </p>
      </div>

      {sessions.length > 0 && (
        <Card title="Check-in codes">
          <p className="mb-3 text-sm text-muted-foreground">
            Show this during the session; attendees enter it on the event page to check in. Check-in
            opens 15 minutes before a session and closes 30 minutes after it ends.
          </p>
          <ul className="grid gap-3 sm:grid-cols-2">
            {sessions.map((s, i) => (
              <li key={s.id} className="rounded-lg bg-muted px-3 py-2">
                <p className="text-sm font-medium">{sessionLabel(s, i)}</p>
                <p className="text-xs text-muted-foreground">
                  {formatSessionTime(s.startsAt, s.endsAt, event.timeZone)}
                </p>
                <p className="mt-1 font-mono text-lg font-semibold tracking-widest">
                  {s.checkInCode}
                </p>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <nav className="-mx-5 overflow-x-auto px-5 [scrollbar-width:none] sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden">
            <div className="flex w-max gap-1 border-b border-border">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilter(f.key)}
                  aria-current={filter === f.key ? 'page' : undefined}
                  className={`-mb-px whitespace-nowrap border-b-2 px-3 py-2.5 text-sm ${filter === f.key ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                >
                  {f.label} ({count(f.key)})
                </button>
              ))}
            </div>
          </nav>
          {emails.length > 0 && <CopyEmailsButton emails={emails} />}
        </div>
        {shown.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {filter === 'registered'
              ? event.status === 'draft'
                ? 'No one yet. Publish the event to open registration.'
                : 'No one has registered yet. Share the link or a poster to spread the word.'
              : filter === 'waitlisted'
                ? data.capacity === null
                  ? 'No waitlist: this event has no seat limit.'
                  : 'No one is waiting.'
                : 'No cancellations.'}
          </p>
        ) : (
          <AttendeeList event={event} sessions={sessions} attendees={shown} />
        )}
      </Card>
    </div>
  );
}

function CopyEmailsButton({ emails }: { emails: string[] }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="secondary"
      className="w-full sm:w-auto"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(emails.join(', '));
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          // Clipboard blocked; nothing else to do.
        }
      }}
    >
      {copied ? <Check className="size-4" aria-hidden /> : <Copy className="size-4" aria-hidden />}
      {copied
        ? 'Copied'
        : `Copy ${emails.length} registered email${emails.length === 1 ? '' : 's'}`}
    </Button>
  );
}

function AttendeeList({
  event,
  sessions,
  attendees,
}: {
  event: EventDraft;
  sessions: EventDraft['sessions'];
  attendees: Attendee[];
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const key = ['eventAttendees', event.id];
  const mark = useMutation({
    mutationFn: (v: { sessionId: string; userId: string; attended: boolean }) =>
      api.markAttendance(v.sessionId, v.userId, v.attended),
    onMutate: (v) => {
      queryClient.setQueryData<EventAttendees>(key, (old) =>
        old
          ? {
              ...old,
              attendees: old.attendees.map((a) =>
                a.userId === v.userId
                  ? {
                      ...a,
                      attended: v.attended
                        ? [...a.attended, v.sessionId]
                        : a.attended.filter((id) => id !== v.sessionId),
                    }
                  : a,
              ),
            }
          : old,
      );
    },
    // Attendance can earn a certificate, so reload for the server's view.
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });
  const pending = (sessionId: string, userId: string) =>
    mark.isPending && mark.variables?.sessionId === sessionId && mark.variables.userId === userId;

  const checkbox = (a: Attendee, sessionId: string, label: string) => (
    <input
      type="checkbox"
      className="size-5 accent-primary"
      aria-label={label}
      checked={a.attended.includes(sessionId)}
      disabled={a.status !== 'registered' || pending(sessionId, a.userId)}
      onChange={(e) => mark.mutate({ sessionId, userId: a.userId, attended: e.target.checked })}
    />
  );
  const name = (a: Attendee) => a.displayName ?? a.email;
  const when = (a: Attendee) => formatDate(a.registeredAt ?? a.queuedAt);

  return (
    <>
      <ErrorText error={mark.error} />
      {/* Phones: one block per person, attendance underneath. */}
      <ul className="divide-y divide-border sm:hidden">
        {attendees.map((a, i) => (
          <li key={a.userId} className="py-3">
            <p className="text-sm font-medium">
              {a.status === 'waitlisted' ? `#${i + 1} · ` : ''}
              {name(a)}
            </p>
            {a.displayName && <p className="break-all text-xs text-muted-foreground">{a.email}</p>}
            <p className="mt-0.5 text-xs text-muted-foreground">
              {STATUS_LABELS[a.status]} {when(a)}
            </p>
            {a.certificate && (
              <div className="mt-1">
                <CertificateBadge />
              </div>
            )}
            {a.status === 'registered' && sessions.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {sessions.map((s, si) => (
                  <label
                    key={s.id}
                    className="flex min-h-10 items-center gap-2 rounded-lg border border-border px-3 text-sm"
                  >
                    {checkbox(a, s.id, `${name(a)} attended ${sessionLabel(s, si)}`)}
                    Session {si + 1}
                  </label>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-2 pr-4 font-medium">Name</th>
              <th className="py-2 pr-4 font-medium">Status</th>
              <th className="py-2 pr-4 font-medium">
                {attendees[0]?.status === 'registered' ? 'Registered' : 'Since'}
              </th>
              {sessions.map((s, si) => (
                <th
                  key={s.id}
                  className="px-2 py-2 text-center font-medium"
                  title={`${sessionLabel(s, si)}: ${formatSessionTime(s.startsAt, s.endsAt, event.timeZone)}`}
                >
                  S{si + 1}
                </th>
              ))}
              <th className="py-2 pl-2 font-medium">
                <span className="sr-only">Certificate</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {attendees.map((a, i) => (
              <tr key={a.userId}>
                <td className="py-2 pr-4">
                  <p className="font-medium">
                    {a.status === 'waitlisted' ? `#${i + 1} · ` : ''}
                    {name(a)}
                  </p>
                  {a.displayName && (
                    <p className="break-all text-xs text-muted-foreground">{a.email}</p>
                  )}
                </td>
                <td className="py-2 pr-4">{STATUS_LABELS[a.status]}</td>
                <td className="whitespace-nowrap py-2 pr-4 text-muted-foreground">{when(a)}</td>
                {sessions.map((s, si) => (
                  <td key={s.id} className="px-2 py-2 text-center">
                    {checkbox(a, s.id, `${name(a)} attended ${sessionLabel(s, si)}`)}
                  </td>
                ))}
                <td className="py-2 pl-2">{a.certificate && <CertificateBadge />}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {sessions.length > 1 && (
          <p className="mt-3 text-xs text-muted-foreground">
            S1, S2… are the sessions in order. Hover a column for its date.
          </p>
        )}
      </div>
    </>
  );
}

function CertificateBadge() {
  return (
    <Badge>
      <Award className="mr-1 size-3" aria-hidden /> Certificate
    </Badge>
  );
}
